import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, ChevronDown } from "lucide-react";
import { SERVICES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import CTABand from "@/components/sections/CTABand";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  return (
    <div data-testid={`service-detail-${slug}`}>
      <Seo
        title={service.title}
        description={`${service.title} — ${service.short}. Solutions complètes pour PME à Montréal et au Québec.`}
        path={`/services/${slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": service.title,
          "description": service.short,
          "provider": {"@type": "LocalBusiness", "name": "Passerelle Numérique Inc.", "url": "https://passerellenumerique.ca/"},
          "areaServed": {"@type": "AdministrativeArea", "name": "Québec, Canada"},
        }}
      />
      <section className="bg-white pt-16 pb-10">
        <div className="mx-auto max-w-5xl px-6 md:px-10">
          <Link to="/services" className="text-sm text-[#00B8D9] font-semibold hover:underline" data-testid="back-to-services">← Tous les services</Link>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-[#0B1F3A] tracking-tighter mt-4">
            {service.title}
          </h1>
          <p className="mt-4 text-lg text-[#4B5563]">{service.short}</p>
        </div>
      </section>

      <section className="bg-[#F5F7FA] py-16">
        <div className="mx-auto max-w-5xl px-6 md:px-10 grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl p-8 border border-gray-200">
            <div className="text-[#00B8D9] text-xs tracking-[0.2em] uppercase font-bold mb-3">Bénéfices</div>
            <h2 className="font-heading font-bold text-2xl text-[#0B1F3A] mb-5">Ce que vous obtenez</h2>
            <ul className="space-y-3">
              {service.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[#4B5563]">
                  <Check className="h-5 w-5 text-[#00B8D9] flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#0B1F3A] text-white rounded-2xl p-8">
            <div className="text-[#00B8D9] text-xs tracking-[0.2em] uppercase font-bold mb-3">Notre processus</div>
            <h2 className="font-heading font-bold text-2xl mb-5">Comment on travaille</h2>
            <ol className="space-y-4">
              {service.process.map((p, i) => (
                <li key={p} className="flex gap-4">
                  <div className="h-8 w-8 rounded-full bg-[#00B8D9] flex items-center justify-center font-bold text-sm flex-shrink-0">{i + 1}</div>
                  <div className="pt-1 font-medium">{p}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-5xl px-6 md:px-10">
          <h2 className="font-heading font-bold text-2xl text-[#0B1F3A] mb-6">Exemples & cas d'usage</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {service.examples.map((e) => (
              <div key={e} className="bg-[#F5F7FA] rounded-xl p-5 border border-gray-200 hover:border-[#00B8D9] transition-colors">
                <div className="font-semibold text-[#0B1F3A]">{e}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F5F7FA] py-16">
        <div className="mx-auto max-w-3xl px-6 md:px-10">
          <h2 className="font-heading font-bold text-2xl text-[#0B1F3A] mb-6">Questions fréquentes</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {service.faq.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-xl border border-gray-200 px-5">
                <AccordionTrigger data-testid={`faq-${slug}-${i}`} className="font-semibold text-[#0B1F3A] hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#4B5563]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTABand title={`Prêt à démarrer avec ${service.title.toLowerCase()} ?`} />
    </div>
  );
}
